import { useQueryClient } from "@tanstack/react-query";

import { QUERY_KEYS } from "@/modules/shared/constants/query-keys.contant";
import { useResolveRequestHook } from "@/modules/shared/hooks/use-resolve-request.hook";
import { useMainStore } from "@/modules/shared/stores/main.store";

import { useOrderFormStore } from "../stores/order-form.store";
import { useDeleteOrderMutation } from "../mutations/delete-order.mutation";
import { useTransmitOrderMutation } from "../mutations/transmit-order.mutation";
import type { Order } from "../types/order.type";

export const useOrderMutations = () => {
	const queryClient = useQueryClient();

	const {
		orderSelected,
		actions: { setOrderSelected },
	} = useOrderFormStore();

	const { setCloseConfirmation } = useMainStore().actions;

	const { checkStatusOnRequest } = useResolveRequestHook();

	const { mutateAsync: transmitOrder, isLoading: isLoadingTransmitOrder } =
		useTransmitOrderMutation({
			callback: async () => {
				queryClient.invalidateQueries([QUERY_KEYS.ORDERS]);
				queryClient.invalidateQueries([QUERY_KEYS.CLIENTS_REPORTS]);
				queryClient.invalidateQueries([QUERY_KEYS.GOALS_REPORTS]);
				queryClient.invalidateQueries([QUERY_KEYS.ORDER_TRANSMISSION]);
				setCloseConfirmation();
				setOrderSelected(undefined);
			},
			resolveRequest: checkStatusOnRequest,
		});
	const { deleteOrder, isLoadingDeleteOrder } = useDeleteOrderMutation({
		callback: async () => {
			setOrderSelected(undefined);

			queryClient.refetchQueries<Order[]>([QUERY_KEYS.ORDERS]);
			const cachedFiltered = queryClient.getQueryData<Order[]>([
				QUERY_KEYS.ORDERS_FILTERED,
			]);

			if (cachedFiltered) {
				const newOrders = cachedFiltered.filter(
					(order) => order.idPedidoCel !== orderSelected?.idPedidoCel,
				);
				queryClient.setQueryData([QUERY_KEYS.ORDERS_FILTERED], newOrders);
			}

			const cachedAllOrders = queryClient.getQueryData<Order[]>([
				QUERY_KEYS.ORDERS,
			]);

			if (cachedAllOrders) {
				const newOrders = cachedAllOrders.filter(
					(order) => order.idPedidoCel !== orderSelected?.idPedidoCel,
				);
				queryClient.setQueryData([QUERY_KEYS.ORDERS], newOrders);
			}
		},
		resolveRequest: checkStatusOnRequest,
	});
	return {
		deleteOrder,
		transmitOrder,
		isLoadingMutations: isLoadingTransmitOrder,
		isLoadingDeleteOrder,
	};
};
